import React from 'react';
import {
  CloudArrowUpIcon,
  DocumentIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  PaperClipIcon,
  RectangleStackIcon
} from '@heroicons/react/20/solid';
import clsx from 'clsx';

import { Button } from '@oysterjs/ui/button';
import { Checkbox } from '@oysterjs/ui/checkbox';
import { Dialog, DialogActions, DialogBody, DialogTitle } from '@oysterjs/ui/dialog';
import { Input, InputGroup } from '@oysterjs/ui/input';
import { Spinner } from '@oysterjs/ui/spinner';

import { useFileDrop } from '~/components/cara-input/use-file-drop';
import { useFilePicker } from '~/components/cara-input/use-file-picker';
import {
  type AttachmentGroup,
  type AttachmentGroupMember,
  canCarryRender,
  isPdfOnlyGroup,
  defaultGroupName,
  groupFileName,
  groupRefusal,
  groupSummary,
  MIN_GROUP_MEMBERS,
  supportsProducerSignature
} from '~/components/workspaces/lib/attachment-group';
import {
  attachmentRefusal,
  formatFileSize,
  resolveAttachmentLimit
} from '~/components/cara-input/cara-attachment';
import {
  PdfAttachmentSection,
  type WorkspacePdfFile
} from './artifact-focus-html-document-render-pdf-attachment';
import { AttachmentCustomizeButton } from './attachment-customize-button';
import { ProducerSignatureOptions, type ProducerSignature } from './pdf-attachment-options';

export const SEARCH_DEBOUNCE_MS = 250;

export type AttachmentPickerArtifact = {
  id: string;
  title: string;
  type: string;
  workspaceId: string;
  workspaceTitle: string | null;
  clientName: string | null;
  updatedAt: string;
};

export type AttachmentPickerFile = { id: string; name: string; size: number };

/**
 * A producer signature the combine builder can stamp onto its document. This is
 * the agency's own details rendered onto a generated form, and not the email
 * signature the composer appends to the message body.
 */
export type AttachmentPickerSignature = ProducerSignature;

export type AttachmentPickerDialogProps = {
  open: boolean;
  onClose: () => void;
  /** Workspace the email being composed lives in; its artifacts sort first. */
  currentWorkspaceId: string;
  /** Already-attached ids, shown as selected and not re-addable. */
  attachedArtifactIds: string[];
  attachedFileIds: string[];
  /** Bytes already committed by attachments outside this dialog, so the ceiling is judged against the whole message. */
  attachedBytes: number;
  /**
   * The server's attachment ceiling. Omitted or non-positive values refuse at
   * `ATTACHMENT_LIMIT_BYTES`, so a dialog opened before the query answers still
   * blocks an oversized file.
   */
  limitBytes?: number;
  /** Files already in this workspace, offered alongside the artifacts. */
  workspaceFiles: AttachmentPickerFile[];
  /** Producer signatures the combine builder offers, labelled by where they come from. */
  agencyProducerInformations?: AttachmentPickerSignature[];
  userProducerInformations?: AttachmentPickerSignature[];
  /** Resolves the artifact list for a query. Debounce is the dialog's job, not the caller's. */
  searchArtifacts: (query: string) => Promise<AttachmentPickerArtifact[]>;
  /** Uploads dropped/chosen files; resolves to the stored files. Rejects on failure. */
  uploadFiles: (files: File[]) => Promise<AttachmentPickerFile[]>;
  /**
   * Renders the sequence into one PDF and stores it, resolving to the stored
   * file. Rejects with a reason safe to show the sender.
   */
  mergeGroup: (
    members: AttachmentGroupMember[],
    name: string,
    anchorId: string,
    producerInformationId: string | null
  ) => Promise<AttachmentPickerFile>;
  /** Combined PDFs already on the draft, so reopening one edits rather than duplicates it. */
  attachedGroups?: AttachmentGroup[];
  /** Opens straight into the builder for this group, by its merged file id. */
  editingGroupFileId?: string | null;
  /** Called once with everything the user picked when they confirm. */
  onConfirm: (picked: {
    artifactIds: string[];
    fileIds: string[];
    groups: AttachmentGroup[];
    /** Groups the sender replaced, whose old merged file is no longer on the email. */
    replacedGroupFileIds: string[];
    /**
     * Attachments the email already carried that a combined PDF now holds. They
     * come off the email, or the recipient gets the document twice: once inside
     * the merged file and once beside it.
     */
    consumedArtifactIds: string[];
    consumedFileIds: string[];
  }) => void;
};

/**
 * A combined PDF as the sender is arranging it: one document with pages before
 * it and pages after it. The anchor is held apart from the two lists rather
 * than found inside one sequence, because which document the PDF is built
 * around is the sender's choice and not an accident of ordering.
 */
type GroupDraft = {
  anchor: AttachmentGroupMember;
  prefix: AttachmentGroupMember[];
  suffix: AttachmentGroupMember[];
};

/** The draft in the order it merges, which is the order the group records. */
const groupSequence = (draft: GroupDraft): AttachmentGroupMember[] => [
  ...draft.prefix,
  draft.anchor,
  ...draft.suffix
];

/** Puts a stored group back into the two sections its anchor sat between. */
const draftAround = (members: AttachmentGroupMember[], anchorId: string): GroupDraft | null => {
  const at = members.findIndex((member) => member.id === anchorId);
  if (at < 0) {
    return null;
  }
  return { anchor: members[at], prefix: members.slice(0, at), suffix: members.slice(at + 1) };
};

/** A ticked row: which list it sits in, and its id within that list. */
type Tick = Pick<AttachmentGroupMember, 'kind' | 'id'>;

const isTick = (tick: Tick, kind: Tick['kind'], id: string) => tick.kind === kind && tick.id === id;

const tickedIds = (ticks: Tick[], kind: Tick['kind']) =>
  ticks.filter((tick) => tick.kind === kind).map((tick) => tick.id);

const DuplicatePagesWarning = ({ id }: { id: string }) => (
  <p
    id={id}
    role="note"
    className="mt-2 flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400"
  >
    <ExclamationTriangleIcon aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
    <span>
      <strong>Possible duplicate pages.</strong> Customized or combined PDFs may already contain
      some of these documents. Review their contents before combining.
    </span>
  </p>
);

/** `DOCUMENT_HTML` reads as "Document html" — enough to tell same-titled artifacts apart. */
const typeLabel = (type: string): string => {
  const words = type.replace(/_/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * Where an artifact from another workspace came from. Both halves are optional
 * on the server, and a row with neither is still attachable, so the caller gets
 * null rather than a subtitle made of separators.
 */
const originLabel = (artifact: AttachmentPickerArtifact): string | null => {
  const parts = [artifact.clientName, artifact.workspaceTitle].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
};

const formatUpdatedAt = (updatedAt: string): string => {
  const date = new Date(updatedAt);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const PickerRow = (props: {
  icon: React.ReactNode;
  title: string;
  subtitle?: string | null;
  meta?: string | null;
  checked: boolean;
  disabled: boolean;
  onToggle: () => void;
  children?: React.ReactNode;
}) => (
  <li
    // Enter on a customization control must not also toggle its attachment.
    onKeyDown={(e) => {
      if (
        e.key === 'Enter' &&
        !props.disabled &&
        (e.target as HTMLElement).matches('input[type="checkbox"], [role="checkbox"]')
      ) {
        e.preventDefault();
        props.onToggle();
      }
    }}
    className={clsx(
      'flex items-center gap-3 rounded-md px-2 py-2',
      props.disabled && !props.children ? 'opacity-60' : 'hover:bg-neutral-50 dark:hover:bg-white/5'
    )}
  >
    <Checkbox
      color="sky"
      checked={props.checked}
      disabled={props.disabled}
      onChange={props.onToggle}
      aria-label={props.title}
    />
    <span className="shrink-0 text-neutral-400 dark:text-neutral-500">{props.icon}</span>
    <span className="min-w-0 flex-1">
      <span className="block truncate text-sm text-neutral-800 dark:text-neutral-200">
        {props.title}
      </span>
      {props.subtitle && (
        <span className="block truncate text-xs text-neutral-500 dark:text-neutral-400">
          {props.subtitle}
        </span>
      )}
    </span>
    {props.meta && (
      <span className="shrink-0 text-xs text-neutral-500 dark:text-neutral-400">{props.meta}</span>
    )}
    {props.children}
  </li>
);

const GroupHeading = ({ children }: { children: React.ReactNode }) => (
  <h3 className="px-2 pb-1 pt-3 text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
    {children}
  </h3>
);

/**
 * Picks what an email carries: workspace artifacts, files already in the
 * workspace, and files uploaded from here.
 *
 * It opens on this email's own workspace alone; documents from elsewhere are
 * reached by searching.
 *
 * A search splits its results into "This workspace" and "Other workspaces" so
 * attaching another client's document stays possible but never accidental —
 * the cross-client rows sit under their own heading and name their client.
 */
export const AttachmentPickerDialog = (props: AttachmentPickerDialogProps) => {
  const {
    open,
    onClose,
    currentWorkspaceId,
    attachedArtifactIds,
    attachedFileIds,
    attachedBytes,
    workspaceFiles,
    onConfirm
  } = props;
  const limitBytes = resolveAttachmentLimit(props.limitBytes);
  const agencyProducerInformations = props.agencyProducerInformations ?? [];
  const userProducerInformations = props.userProducerInformations ?? [];

  const [query, setQuery] = React.useState('');
  const [settledQuery, setSettledQuery] = React.useState('');
  const [results, setResults] = React.useState<AttachmentPickerArtifact[]>([]);
  const [searching, setSearching] = React.useState(false);
  const [searchError, setSearchError] = React.useState<string | null>(null);
  /**
   * Every row ticked here, documents and files alike, in the order they were
   * ticked. It is one list because a combine joins PDFs in this order: kept as
   * two, a ticked upload would always land after a ticked PDF artifact.
   */
  const [ticks, setTicks] = React.useState<Tick[]>([]);
  const selectedArtifactIds = tickedIds(ticks, 'artifact');
  const selectedFileIds = tickedIds(ticks, 'file');
  const [uploadedFiles, setUploadedFiles] = React.useState<AttachmentPickerFile[]>([]);
  const [uploading, setUploading] = React.useState<AttachmentPickerFile[]>([]);
  const [uploadError, setUploadError] = React.useState<string | null>(null);
  const [refusals, setRefusals] = React.useState<string[]>([]);
  /** The combined PDF being arranged, or null when the builder is closed. */
  const [group, setGroup] = React.useState<GroupDraft | null>(null);
  const [groupName, setGroupName] = React.useState('');
  /** Set when the builder was opened on a group already on the draft. */
  const [replacingFileId, setReplacingFileId] = React.useState<string | null>(null);
  /** Producer signature the combined PDF's document is rendered with, or null for none. */
  const [producerInformationId, setProducerInformationId] = React.useState<string | null>(null);
  const [merging, setMerging] = React.useState(false);
  const [mergeError, setMergeError] = React.useState<string | null>(null);
  const [builtGroups, setBuiltGroups] = React.useState<AttachmentGroup[]>([]);
  const [replacedGroupFileIds, setReplacedGroupFileIds] = React.useState<string[]>([]);
  const retainedAttachedBytes =
    attachedBytes -
    (props.attachedGroups ?? [])
      .filter((group) => replacedGroupFileIds.includes(group.fileId))
      .reduce((sum, group) => sum + group.size, 0);
  const consumedArtifactIds = attachedArtifactIds.filter((id) =>
    builtGroups.some((group) =>
      group.members.some((member) => member.kind === 'artifact' && member.id === id)
    )
  );
  const consumedFileIds = attachedFileIds.filter((id) =>
    builtGroups.some((group) =>
      group.members.some((member) => member.kind === 'file' && member.id === id)
    )
  );

  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const uploadKey = React.useRef(0);

  // Callers hand these in as inline closures, so depending on them directly
  // would re-run the search on every render of the composer.
  const searchArtifactsRef = React.useRef(props.searchArtifacts);
  const uploadFilesRef = React.useRef(props.uploadFiles);
  React.useEffect(() => {
    searchArtifactsRef.current = props.searchArtifacts;
    uploadFilesRef.current = props.uploadFiles;
  });

  React.useEffect(() => {
    if (!open) return;
    setQuery('');
    setSettledQuery('');
    setResults([]);
    setSearchError(null);
    setTicks([]);
    setUploadedFiles([]);
    setUploading([]);
    setUploadError(null);
    setRefusals([]);
    setGroupName('');
    setProducerInformationId(null);
    setMerging(false);
    setMergeError(null);
    setBuiltGroups([]);
    setReplacedGroupFileIds([]);
    const editing = props.attachedGroups?.find(
      (candidate) => candidate.fileId === props.editingGroupFileId
    );
    setGroup(editing ? draftAround(editing.members, editing.anchorId) : null);
    setReplacingFileId(editing ? editing.fileId : null);
    if (editing) {
      setGroupName(editing.name);
      setProducerInformationId(editing.producerInformationId ?? null);
    }
    searchInputRef.current?.focus();
  }, [open, props.editingGroupFileId]);

  React.useEffect(() => {
    const timer = setTimeout(() => setSettledQuery(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const searchId = React.useRef(0);
  React.useEffect(() => {
    if (!open) return;
    const id = ++searchId.current;
    setSearching(true);
    void searchArtifactsRef.current(settledQuery).then(
      (artifacts) => {
        // A slow response to an abandoned query would otherwise overwrite the
        // results the user is looking at.
        if (id !== searchId.current) return;
        setResults(artifacts);
        setSearchError(null);
        setSearching(false);
      },
      () => {
        if (id !== searchId.current) return;
        setResults([]);
        setSearchError('Could not load documents to attach. Try again.');
        setSearching(false);
      }
    );
  }, [open, settledQuery]);

  const fileById = React.useMemo(() => {
    const byId = new Map<string, AttachmentPickerFile>();
    [...workspaceFiles, ...uploadedFiles].forEach((file) => byId.set(file.id, file));
    return byId;
  }, [workspaceFiles, uploadedFiles]);

  /** What this dialog has committed so far. Uploads in flight count: their bytes are already spoken for. */
  const pickedBytes =
    selectedFileIds.reduce((sum, id) => sum + (fileById.get(id)?.size ?? 0), 0) +
    uploading.reduce((sum, pending) => sum + pending.size, 0) +
    builtGroups.reduce((sum, group) => sum + group.size, 0);

  const addFiles = (files: File[]) => {
    if (merging) return;
    const accepted: File[] = [];
    const refused: string[] = [];
    // Each file is weighed against everything already ticked plus the ones
    // accepted ahead of it in this batch: two that each fit can overflow together.
    let bytes = retainedAttachedBytes + pickedBytes;
    files.forEach((file) => {
      const refusal = attachmentRefusal({
        name: file.name,
        size: file.size,
        attachedBytes: bytes,
        limitBytes
      });
      if (refusal) {
        refused.push(refusal);
        return;
      }
      bytes += file.size;
      accepted.push(file);
    });

    setRefusals(refused);
    if (accepted.length === 0) return;

    const pending = accepted.map((file) => ({
      id: `pending-${uploadKey.current++}`,
      name: file.name,
      size: file.size
    }));
    setUploadError(null);
    setUploading((prev) => [...prev, ...pending]);

    const settle = () =>
      setUploading((prev) => prev.filter((row) => !pending.some((p) => p.id === row.id)));

    void uploadFilesRef.current(accepted).then(
      (stored) => {
        settle();
        setUploadedFiles((prev) => [
          ...prev,
          ...stored.filter((s) => !prev.some((p) => p.id === s.id))
        ]);
        setTicks((prev) => [
          ...prev,
          ...stored
            .filter(
              (s) =>
                !prev.some((tick) => isTick(tick, 'file', s.id)) && !attachedFileIds.includes(s.id)
            )
            .map((s) => ({ kind: 'file' as const, id: s.id }))
        ]);
      },
      () => {
        settle();
        setUploadError('That upload failed. Try again.');
      }
    );
  };

  const picker = useFilePicker({ onFiles: addFiles });
  const { isDragging, handlers } = useFileDrop({ onDrop: addFiles, disabled: merging });

  const trimmedQuery = settledQuery.trim();

  const files = React.useMemo(() => {
    const byId = new Map<string, AttachmentPickerFile>();
    workspaceFiles
      .filter((file) => file.name.toLowerCase().includes(trimmedQuery.toLowerCase()))
      .forEach((file) => byId.set(file.id, file));
    // Files uploaded from this dialog ignore the query: the user added them
    // seconds ago, and hiding them reads as a failed upload.
    uploadedFiles.forEach((file) => byId.set(file.id, file));
    return [...byId.values()];
  }, [workspaceFiles, uploadedFiles, trimmedQuery]);

  const currentArtifacts = results.filter((a) => a.workspaceId === currentWorkspaceId);
  // Held back here rather than at the request, because the rows this email's
  // own workspace contributes come from the same read.
  const otherArtifacts = trimmedQuery
    ? results.filter((a) => a.workspaceId !== currentWorkspaceId)
    : [];

  const toggleArtifact = (artifactId: string) =>
    setTicks((prev) =>
      prev.some((tick) => isTick(tick, 'artifact', artifactId))
        ? prev.filter((tick) => !isTick(tick, 'artifact', artifactId))
        : [...prev, { kind: 'artifact', id: artifactId }]
    );

  const toggleFile = (fileId: string) => {
    if (selectedFileIds.includes(fileId)) {
      setRefusals([]);
      setTicks((prev) => prev.filter((tick) => !isTick(tick, 'file', fileId)));
      return;
    }

    const candidate = fileById.get(fileId);
    // Bytes already in the workspace still have to clear the ceiling: nothing
    // is uploaded, but the same bytes will travel.
    const refusal =
      candidate &&
      attachmentRefusal({
        name: candidate.name,
        size: candidate.size,
        attachedBytes: retainedAttachedBytes + pickedBytes,
        limitBytes
      });
    if (refusal) {
      setRefusals([refusal]);
      return;
    }

    setRefusals([]);
    setTicks((prev) => [...prev, { kind: 'file', id: fileId }]);
  };

  const artifactRow = (artifact: AttachmentPickerArtifact, showOrigin: boolean) => {
    const customized =
      builtGroups.find((candidate) => candidate.anchorId === artifact.id) ??
      props.attachedGroups?.find(
        (candidate) =>
          candidate.anchorId === artifact.id && !replacedGroupFileIds.includes(candidate.fileId)
      );
    const attached = attachedArtifactIds.includes(artifact.id) || !!customized;
    const document: AttachmentGroupMember = {
      id: artifact.id,
      kind: 'artifact',
      name: artifact.title,
      type: artifact.type
    };
    return (
      <PickerRow
        key={artifact.id}
        icon={<DocumentIcon className="size-4" />}
        title={artifact.title}
        subtitle={showOrigin ? originLabel(artifact) : typeLabel(artifact.type)}
        meta={formatUpdatedAt(artifact.updatedAt)}
        checked={attached || selectedArtifactIds.includes(artifact.id)}
        disabled={attached || merging}
        onToggle={() => toggleArtifact(artifact.id)}
      >
        {artifact.workspaceId === currentWorkspaceId && canCarryRender(document) && (
          <AttachmentCustomizeButton
            document={document}
            candidates={groupCandidates}
            agencyProducerInformations={agencyProducerInformations}
            userProducerInformations={userProducerInformations}
            initialGroup={customized}
            disabled={merging}
            onSavingChange={setMerging}
            onError={setMergeError}
            uploadFiles={props.uploadFiles}
            onSave={(members, signatureId) =>
              storeGroup(
                members,
                customized?.name ?? defaultGroupName([document]),
                document.id,
                signatureId,
                customized?.fileId ?? null
              )
            }
          />
        )}
      </PickerRow>
    );
  };

  /**
   * Everything a combined PDF may hold. A document from another workspace is
   * absent because the merge renders each member inside the workspace the email
   * is being written in, and a non-PDF file is absent because there is no way
   * to concatenate it — both would be a merge that failed after the wait.
   */
  const groupCandidates: AttachmentGroupMember[] = React.useMemo(() => {
    const artifacts: AttachmentGroupMember[] = results
      .filter((a) => a.workspaceId === currentWorkspaceId)
      .map((a) => ({ id: a.id, kind: 'artifact', name: a.title, type: a.type }));
    const pdfs: AttachmentGroupMember[] = [...workspaceFiles, ...uploadedFiles]
      .filter((f) => f.name.toLowerCase().endsWith('.pdf'))
      .map((f) => ({ id: f.id, kind: 'file', name: f.name }));
    return [...artifacts, ...pdfs];
  }, [results, currentWorkspaceId, workspaceFiles, uploadedFiles]);

  const groupCandidateById = React.useMemo(() => {
    const byId = new Map<string, AttachmentGroupMember>();
    groupCandidates.forEach((member) => byId.set(member.id, member));
    return byId;
  }, [groupCandidates]);

  /** The section speaks in `WorkspacePdfFile`, which is the same row under another name. */
  const groupCatalog: WorkspacePdfFile[] = React.useMemo(
    () =>
      groupCandidates.map((member) => ({
        id: member.id,
        kind: member.kind,
        name: member.name,
        mimeType: 'application/pdf'
      })),
    [groupCandidates]
  );

  /** What the ticked rows would combine into, in the order they were ticked. */
  const tickedMembers = ticks.flatMap((tick) => {
    const member = groupCandidateById.get(tick.id);
    return member?.kind === tick.kind ? [member] : [];
  });

  // Only a known customized PDF in this combine selection warrants the warning.
  // A PDF extension alone does not tell us whether the file was customized.
  const customizedFileIds = new Set(
    [...builtGroups, ...(props.attachedGroups ?? [])].map((candidate) => candidate.fileId)
  );
  const combineMembers = group ? groupSequence(group) : tickedMembers;
  const combineWarningId = React.useId();
  const showCombineWarning =
    combineMembers.length >= MIN_GROUP_MEMBERS &&
    combineMembers.some((member) => member.kind === 'file' && customizedFileIds.has(member.id));

  /**
   * Combining is offered for PDFs and nothing else: two or more, every one of
   * them already a PDF. A ticked document is left out deliberately — merging it
   * would flatten a document that is re-rendered when the email is sent. A lone
   * document is still customized through its own Customize button.
   */
  const canCombineTicked = isPdfOnlyGroup(tickedMembers);

  /**
   * PDFs merge by plain concatenation, so there is nothing to arrange first:
   * the ticked PDFs are combined straight away, in the order they were ticked,
   * under a name taken from the first. Nothing is rendered, so no member anchors
   * the merge; the first PDF simply leads the sequence.
   */
  const combineTicked = async () => {
    const members = tickedMembers;
    const [first] = members;
    if (!first || merging || !isPdfOnlyGroup(members)) {
      return;
    }
    setMerging(true);
    setMergeError(null);
    try {
      await storeGroup(members, defaultGroupName(members), first.id, null, null);
    } catch (err) {
      setMergeError(
        err instanceof Error ? err.message : 'We could not combine these into one PDF.'
      );
    } finally {
      setMerging(false);
    }
  };

  const closeGroupBuilder = () => {
    setGroup(null);
    setReplacingFileId(null);
    setMergeError(null);
  };

  /**
   * Puts the sequence back on the picker as ordinary ticks. A group that was
   * already on the draft is also marked for removal, so confirming leaves the
   * email carrying its documents rather than both them and the merged copy.
   */
  const dissolveGroupBuilder = () => {
    const members = group ? groupSequence(group) : [];
    // A member the email already carries stays carried: ticking it again would
    // count it twice on the way out.
    setTicks(
      members
        .filter(
          (m) => !(m.kind === 'artifact' ? attachedArtifactIds : attachedFileIds).includes(m.id)
        )
        .map((m) => ({ kind: m.kind, id: m.id }))
    );
    if (replacingFileId) {
      setReplacedGroupFileIds((prev) => [...prev, replacingFileId]);
    }
    closeGroupBuilder();
  };

  const reorderGroup = (prefixIds: string[], suffixIds: string[]) =>
    setGroup((prev) => {
      if (!prev) {
        return prev;
      }
      const resolve = (ids: string[]) =>
        ids.flatMap((id) => {
          const member = groupCandidateById.get(id) ?? groupSequence(prev).find((m) => m.id === id);
          return member ? [member] : [];
        });
      return { anchor: prev.anchor, prefix: resolve(prefixIds), suffix: resolve(suffixIds) };
    });

  /** A PDF dropped on a section is stored first: the merge names members by id. */
  const addGroupFiles = (dropped: File[], section: 'before' | 'after') => {
    setMergeError(null);
    void uploadFilesRef.current(dropped).then(
      (stored) => {
        setUploadedFiles((prev) => [
          ...prev,
          ...stored.filter((s) => !prev.some((p) => p.id === s.id))
        ]);
        const added = stored.map((s) => ({ id: s.id, kind: 'file' as const, name: s.name }));
        setGroup((prev) => {
          if (!prev) {
            return prev;
          }
          return section === 'before'
            ? { ...prev, prefix: [...prev.prefix, ...added] }
            : { ...prev, suffix: [...prev.suffix, ...added] };
        });
      },
      () => setMergeError('That upload failed, so it is not in the combined PDF.')
    );
  };

  const storeGroup = async (
    members: AttachmentGroupMember[],
    name: string,
    anchorId: string,
    signatureId: string | null,
    replacingId: string | null
  ) => {
    const supportedSignatureId = supportsProducerSignature(
      members.find((member) => member.id === anchorId)
    )
      ? signatureId
      : null;
    const consumedFiles = members
      .filter((m) => m.kind === 'file' && attachedFileIds.includes(m.id))
      .map((m) => m.id);
    const merged = await props.mergeGroup(
      members,
      groupFileName(name),
      anchorId,
      supportedSignatureId
    );
    const replaced = [...(props.attachedGroups ?? []), ...builtGroups].find(
      (candidate) => candidate.fileId === replacingId
    );
    const selectedMemberBytes = members.reduce(
      (sum, member) =>
        sum +
        (member.kind === 'file' && selectedFileIds.includes(member.id)
          ? (fileById.get(member.id)?.size ?? 0)
          : 0),
      0
    );
    const overweight = attachmentRefusal({
      name: merged.name,
      size: merged.size,
      attachedBytes:
        retainedAttachedBytes +
        pickedBytes -
        (replaced?.size ?? 0) -
        selectedMemberBytes -
        consumedFiles.reduce((sum, id) => sum + (fileById.get(id)?.size ?? 0), 0),
      limitBytes
    });
    if (overweight) throw new Error(overweight);

    setBuiltGroups((prev) => [
      ...prev.filter(
        (candidate) => candidate.fileId !== merged.id && candidate.fileId !== replacingId
      ),
      {
        fileId: merged.id,
        name: merged.name,
        size: merged.size,
        members,
        anchorId,
        ...(supportedSignatureId ? { producerInformationId: supportedSignatureId } : {})
      }
    ]);
    if (
      replacingId &&
      props.attachedGroups?.some((candidate) => candidate.fileId === replacingId)
    ) {
      setReplacedGroupFileIds((prev) => [...prev, replacingId]);
    }
    setTicks((prev) => prev.filter((tick) => !members.some((m) => isTick(tick, m.kind, m.id))));
  };

  const combineGroup = async () => {
    if (!group) return;
    const members = groupSequence(group);
    const refusal = groupRefusal(members, group.anchor.id, producerInformationId);
    if (refusal) {
      setMergeError(refusal);
      return;
    }
    setMerging(true);
    setMergeError(null);
    try {
      await storeGroup(members, groupName, group.anchor.id, producerInformationId, replacingFileId);
      closeGroupBuilder();
    } catch (err) {
      setMergeError(
        err instanceof Error ? err.message : 'We could not combine these into one PDF.'
      );
    } finally {
      setMerging(false);
    }
  };

  const pendingCount = ticks.length + builtGroups.length;
  const nothingToShow =
    currentArtifacts.length === 0 &&
    otherArtifacts.length === 0 &&
    files.length === 0 &&
    uploading.length === 0;

  if (group) {
    const groupMembers = groupSequence(group);
    const refusal = groupRefusal(groupMembers, group.anchor.id, producerInformationId);
    return (
      // The sequence grows with every page added, so the panel is held inside
      // the viewport and the body scrolls: the button that commits the merge
      // stays in sight rather than sliding off the bottom of a taller dialog.
      <Dialog
        open={open}
        onClose={onClose}
        size="2xl"
        className="flex max-h-[calc(100dvh-4rem)] flex-col"
      >
        <DialogTitle className="shrink-0">
          {replacingFileId ? 'Edit this combined PDF' : 'Combine into one PDF'}
        </DialogTitle>
        <DialogBody className="min-h-0 flex-1 overflow-y-auto">
          {/* Separately attached documents re-render at send time; a combined PDF cannot. */}
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            Pages go before and after this document, and are merged into a single PDF now. The
            combined file goes out as it is merged here — later edits to these documents do not
            reach it.
          </p>

          <div className="mt-4">
            <label
              htmlFor="combined-pdf-name"
              className="block text-xs font-medium text-neutral-500 dark:text-neutral-400"
            >
              Filename the recipient sees
            </label>
            <Input
              id="combined-pdf-name"
              name="combined-pdf-name"
              aria-label="Filename the recipient sees"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
            />
          </div>

          {supportsProducerSignature(group.anchor) &&
            (agencyProducerInformations.length > 0 || userProducerInformations.length > 0) && (
              <div className="mt-4">
                <ProducerSignatureOptions
                  agencyProducerInformations={agencyProducerInformations}
                  userProducerInformations={userProducerInformations}
                  value={producerInformationId}
                  onChange={setProducerInformationId}
                  description="Choose a signature to include on this document."
                />
              </div>
            )}

          <div className="mt-4">
            <PdfAttachmentSection
              documentName={group.anchor.name}
              catalogLabel="Add another document"
              dropZoneLabel="Add a PDF to this combined file"
              // The document in the middle is not something to add beside
              // itself, so it is the one row the catalog withholds.
              attachmentCatalog={groupCatalog.filter((entry) => entry.id !== group.anchor.id)}
              prefixFileIds={group.prefix.map((member) => member.id)}
              suffixFileIds={group.suffix.map((member) => member.id)}
              onChange={reorderGroup}
              onAddFiles={addGroupFiles}
            />
          </div>

          {mergeError ? (
            <p className="mt-3 text-sm text-red-600 dark:text-red-500">{mergeError}</p>
          ) : (
            // Said before the sender reaches for a button that cannot be pressed.
            refusal && (
              <p className="mt-3 text-sm text-neutral-500 dark:text-neutral-400">{refusal}</p>
            )
          )}
        </DialogBody>

        {showCombineWarning && <DuplicatePagesWarning id={combineWarningId} />}
        <DialogActions className="mt-4">
          <Button type="button" color="white" onClick={dissolveGroupBuilder}>
            Keep them separate
          </Button>
          <Button
            type="button"
            color="sky"
            disabled={merging || !!refusal}
            aria-describedby={showCombineWarning ? combineWarningId : undefined}
            onClick={combineGroup}
          >
            {merging
              ? 'Combining…'
              : groupMembers.length === 1
                ? 'Save attachment'
                : `Combine ${groupMembers.length} into one PDF`}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!merging) onClose();
      }}
      size="2xl"
    >
      <DialogTitle>Attach to this email</DialogTitle>
      <DialogBody className="mt-4">
        {merging && (
          <div
            role="status"
            className="mb-3 flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400"
          >
            <Spinner size="size-4" color="neutral" />
            <span>
              Preparing PDF… Attachment selection and uploads are paused until it finishes.
            </span>
          </div>
        )}
        {mergeError && (
          <p role="alert" className="mb-3 text-sm text-red-600 dark:text-red-500">
            {mergeError}
          </p>
        )}
        <InputGroup>
          <MagnifyingGlassIcon />
          <Input
            ref={searchInputRef}
            name="attachment-search"
            aria-label="Search documents"
            placeholder="Search documents in any workspace&hellip;"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </InputGroup>

        <div className="mt-3 max-h-[45vh] min-h-[12rem] overflow-y-auto">
          {searching && (
            <div className="flex items-center gap-2 py-8 text-sm text-neutral-500 dark:text-neutral-400">
              <Spinner size="size-4" color="neutral" />
              Searching&hellip;
            </div>
          )}

          {!searching && !searchError && nothingToShow && (
            <p className="py-8 text-sm text-neutral-500 dark:text-neutral-400">
              {trimmedQuery
                ? `No documents match “${trimmedQuery}”.`
                : 'Nothing in this workspace yet. Search to attach a document from another workspace, or drop a file below.'}
            </p>
          )}

          {!searching && (
            <>
              {(searchError ||
                currentArtifacts.length > 0 ||
                files.length > 0 ||
                uploading.length > 0) && (
                <>
                  <GroupHeading>This workspace</GroupHeading>
                  {/* The failure belongs to the artifact search alone. The files
                    below come from the caller and from this dialog's own
                    uploads, so they stay listed and attachable. */}
                  {searchError && (
                    <p className="px-2 py-2 text-sm text-red-600 dark:text-red-500">
                      {searchError}
                    </p>
                  )}
                  <ul>
                    {currentArtifacts.map((artifact) => artifactRow(artifact, false))}
                    {files.map((file) => {
                      const attached = attachedFileIds.includes(file.id);
                      return (
                        <PickerRow
                          key={file.id}
                          icon={<PaperClipIcon className="size-4" />}
                          title={file.name}
                          meta={formatFileSize(file.size)}
                          checked={attached || selectedFileIds.includes(file.id)}
                          disabled={attached || merging}
                          onToggle={() => toggleFile(file.id)}
                        />
                      );
                    })}
                    {uploading.map((pending) => (
                      <li
                        key={pending.id}
                        className="flex items-center gap-3 px-2 py-2 text-sm text-neutral-500 dark:text-neutral-400"
                      >
                        <Spinner size="size-4" color="neutral" />
                        <span className="min-w-0 flex-1 truncate">{pending.name}</span>
                        <span className="shrink-0 text-xs">{formatFileSize(pending.size)}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {otherArtifacts.length > 0 && (
                <>
                  <GroupHeading>Other workspaces</GroupHeading>
                  <ul>{otherArtifacts.map((artifact) => artifactRow(artifact, true))}</ul>
                </>
              )}
            </>
          )}
        </div>

        {builtGroups.length > 0 && (
          <ul className="mt-3 space-y-1">
            {builtGroups.map((group) => (
              <li
                key={group.fileId}
                className="flex items-center gap-2 rounded-md bg-neutral-50 dark:bg-neutral-900 px-2 py-2 text-sm"
              >
                <RectangleStackIcon className="size-4 shrink-0 text-neutral-400 dark:text-neutral-500" />
                <span className="min-w-0 flex-1 truncate">{group.name}</span>
                <span className="shrink-0 text-xs text-neutral-500 dark:text-neutral-400">
                  {groupSummary(group)}
                </span>
                <Button
                  type="button"
                  plain
                  disabled={merging}
                  aria-label={`Remove ${group.name}`}
                  onClick={() =>
                    setBuiltGroups((prev) =>
                      prev.filter((candidate) => candidate.fileId !== group.fileId)
                    )
                  }
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}

        {canCombineTicked && (
          <div className="mt-3">
            <Button
              type="button"
              color="white"
              disabled={merging}
              aria-describedby={showCombineWarning ? combineWarningId : undefined}
              onClick={() => void combineTicked()}
            >
              <RectangleStackIcon className="size-4" />
              Combine {tickedMembers.length} into one PDF
            </Button>
            {showCombineWarning && <DuplicatePagesWarning id={combineWarningId} />}
          </div>
        )}

        <div
          {...handlers}
          data-testid="attachment-drop-zone"
          aria-disabled={merging}
          onClick={merging ? undefined : picker.open}
          className={clsx(
            'mt-4 flex flex-col items-center justify-center gap-1 rounded-lg p-4',
            merging ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
            'transition-all duration-100',
            isDragging
              ? 'outline outline-2 outline-primary-500'
              : 'outline-dashed outline-1 outline-neutral-950/10 dark:outline-white/10 hover:outline-neutral-950/20 dark:hover:outline-white/20'
          )}
        >
          <div className="pointer-events-none flex flex-col items-center justify-center">
            <CloudArrowUpIcon className="size-6 text-neutral-500 dark:text-neutral-400" />
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              Drag and drop files here, or click to choose
            </p>
          </div>
        </div>
        <input {...picker.inputProps} disabled={merging} />

        {uploadError && (
          <p className="mt-2 text-sm text-red-600 dark:text-red-500">{uploadError}</p>
        )}
        {refusals.map((refusal) => (
          <p key={refusal} className="mt-2 text-sm text-red-600 dark:text-red-500">
            {refusal}
          </p>
        ))}
      </DialogBody>

      <DialogActions className="mt-4">
        <Button type="button" color="white" disabled={merging} onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          color="sky"
          disabled={
            (pendingCount === 0 && replacedGroupFileIds.length === 0) ||
            uploading.length > 0 ||
            merging
          }
          onClick={() => {
            onConfirm({
              artifactIds: selectedArtifactIds,
              fileIds: selectedFileIds,
              groups: builtGroups,
              replacedGroupFileIds,
              consumedArtifactIds,
              consumedFileIds
            });
            onClose();
          }}
        >
          {pendingCount === 0 && replacedGroupFileIds.length > 0
            ? 'Save changes'
            : `Attach (${pendingCount})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
